<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  GpBtn,
  GpBtnLink,
  GpExpandTransition,
  GpFadeTransition,
  GpTable,
  GpTableColumn,
  type GpTableDataRow,
  GpTextInput,
} from '@gportal/uikit';
import useGpTableData from '@panel/composables/useGpTableData';
import SinglePageLayout from '@panel/layouts/Layout.SinglePage.vue';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import usePowerAction from 'gpcore-apikit/store/pinia/features/node/usePowerAction';
import { useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { type Image, OperatingSystemFamily } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { type Node, NodeSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import { type Datacenter as DatacenterType } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import {
  ServerPowerState,
  ServerProvisioningState,
} from 'gpcore-grpc/gpcore/api/cloud/v1/server_pb';
import type { SearchOptions, SearchRequest } from 'gpcore-grpc/gpcore/type/v1/search_pb';
import Datacenter from 'gpcore-uikit/components/Component.Badge.Datacenter.vue';
import NodeStatus from 'gpcore-uikit/components/Component.Node.Status.vue';
import Tags from 'gpcore-uikit/components/Component.Node.Tags.vue';
import Toolbar from 'gpcore-uikit/components/Component.Toolbar.vue';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import SearchForm from 'gpcore-uikit/components/new/search/Form.vue';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import ServerMonitoringState from 'gpcore-uikit/components/Component.Server.MonitoringState.vue';
import type { MonitoringState } from 'gpcore-grpc/gpcore/type/v1/monitoring_pb';
import { useDebounceFn } from '@vueuse/core';
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import NodeListToOverviewLink from '@panel/components/node/list/NodeListToOverviewLink.vue';
import { create } from '@bufbuild/protobuf';
import type {
  DestroyNodeResponse,
  PowerActionNodeResponse,
} from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';

enum ModalAction {
  HARD_RESET = 1,
  DESTROY = 2,
}

interface NodesTableDataRow extends GpTableDataRow {
  key: string;
  title: {
    title: string;
    id: string;
  };
  cloudProvider: CloudProviderType;
  status: {
    node: Node;
  };
  ips: string;
  tags: { [key: string]: string };
  os?: Image;
  flavour: string;
  monitoringState: MonitoringState;
  datacenter: {
    obj?: DatacenterType;
  };
}

useProjectViewTitle('Nodes');

const isLoading = ref<boolean>(true);

const projectStore = useProjectStore();
const canForceDeleteNodes = computed(() => projectStore.project?.canForceDeleteNodes ?? false);

const searchTerm = ref(null as string | null);
const extendedSearchRequest = ref<SearchRequest>();
const extendedSearchOptions = ref<SearchOptions>();
const showExtendedSearchOptions = ref<boolean>(false);

const pagination = reactive({ currentPage: 1, totalPages: 1 });

const nodeStore = useNodeStore();
const nodes = computed(() => nodeStore.nodes);
const { addNotification } = useNotifications();
const route = useRoute();

const toTableRows = (items: Node[]): NodesTableDataRow[] =>
  items.map((item) => ({
    tags: item.tags,
    key: item.id,
    selectable:
      (item.status === ServerProvisioningState.ACTIVE ||
        item.status === ServerProvisioningState.RESCUE) &&
      (item.billingPeriod !== BillingPeriod.MONTHLY || canForceDeleteNodes.value),
    title: {
      title: item.fqdn,
      id: item.id,
    },
    cloudProvider: item.cloudProvider == null ? null : item.cloudProvider.type,
    status: { node: item },
    ips: item.networkInterfaces.map((ip) => ip.ipAddresses).join(', '),
    os: item.image,
    flavour: item.flavour.name,
    monitoringState: item.monitoringState,
    datacenter: {
      obj: item.datacenter,
    },
  }));

const { selectedRows: selected, tableData } = useGpTableData(nodes, toTableRows);

// Re-run table mapping when canForceDeleteNodes changes so that selectable
// reflects the current project flag without waiting for a node list reload.
watch(canForceDeleteNodes, () => {
  tableData.value = toTableRows(nodes.value);
});

const reload = useDebounceFn((page: number) => {
  pagination.currentPage = page;
  isLoading.value = true;
  nodeStore
    .reloadNodes(page as number, searchTerm.value, extendedSearchRequest.value)
    .catch((error: ServiceError) => {
      addNotification({
        type: 'danger',
        message: error.message,
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}, 300);

const searchNode = (search: string) => {
  searchTerm.value = search;
  reload(1);
};

const modal = reactive({
  show: false,
  action: ModalAction.HARD_RESET || ModalAction.DESTROY,
  nodes: new Array<string>(),
});

const modalQuestion = computed(() => {
  switch (modal.action) {
    case ModalAction.HARD_RESET:
      return `Do you really want to power reset ${modal.nodes.length} ${
        modal.nodes.length !== 1 ? 'nodes' : 'node'
      }?`;
    case ModalAction.DESTROY:
      return `Do you really want to destroy ${modal.nodes.length} ${
        modal.nodes.length !== 1 ? 'nodes' : 'node'
      }?${canForceDeleteNodes.value ? ' Monthly-billed nodes will be force deleted and any remaining paid time will not be refunded.' : ''}`;
    default:
      return '';
  }
});

const modalButtonText = computed(() => {
  switch (modal.action) {
    case ModalAction.HARD_RESET:
      return 'Power reset';
    default:
      return 'Destroy';
  }
});

const modalButtonClass = computed(() => {
  switch (modal.action) {
    case ModalAction.HARD_RESET:
      return 'warning';
    default:
      return 'danger';
  }
});

const openModal = (action: ModalAction, nodes: Array<NodesTableDataRow>) => {
  modal.show = true;
  modal.action = action;
  modal.nodes = nodes.map((n) => n.key);
};

const openExtendedSearch = () => {
  searchTerm.value = undefined;
  nodeStore
    .getExtendedSearchOptions()
    .then((response) => {
      searchTerm.value = undefined;
      extendedSearchOptions.value = response.options;
      showExtendedSearchOptions.value = true;
    })
    .catch((error: ServiceError) => {
      addNotification({
        type: 'danger',
        message: error.message,
      });
    });
};

const toggleExtendedSearch = () => {
  if (showExtendedSearchOptions.value) {
    extendedSearchRequest.value = undefined;
    showExtendedSearchOptions.value = false;
    reload(1);
  } else {
    openExtendedSearch();
  }
};

const performExtendedSearch = (request?: SearchRequest) => {
  extendedSearchRequest.value = request;
  reload(1);
};

const powerAction = usePowerAction();
const confirmModal = () => {
  modal.show = false;
  const promises = new Array<Promise<DestroyNodeResponse | PowerActionNodeResponse>>();

  modal.nodes.forEach((node) => {
    const resource = create(NodeSchema);
    resource.id = node;

    switch (modal.action) {
      case ModalAction.HARD_RESET:
        promises.push(
          powerAction({
            node: resource,
            action: ServerPowerState.RESET,
          }),
        );
        break;
      case ModalAction.DESTROY:
        promises.push(nodeStore.destroyNode(resource));
        break;
      default:
        break;
    }
  });

  Promise.all(promises).catch((error: ServiceError) => {
    addNotification({ message: error.message, type: 'danger' });
  });
};

onMounted(() => {
  if (route.query['node.extended'] === 'true') {
    openExtendedSearch();
  }

  reload(1);
});
</script>

<template>
  <SinglePageLayout>
    <template #toolbar>
      <Toolbar title="Nodes" :is-selecting="selected.length > 0">
        <template #toolbarMainAction>
          <router-link :to="{ name: 'ProjectNodeCreate' }" v-slot="{ href, navigate }" custom>
            <GpBtnLink
              :href="href"
              size="sm"
              :icon="['fas', 'fa-plus']"
              label="Create"
              v-playwright="'nodes-create-button'"
              @click="navigate"
            />
          </router-link>
        </template>
        <template #toolbarSearch>
          <GpFadeTransition>
            <GpTextInput
              v-if="!showExtendedSearchOptions"
              class="tw-w-full md:tw-w-60"
              :model-value="searchTerm"
              input-id="search-term"
              name="search-term"
              type="text"
              autocomplete="off"
              transparent
              placeholder="Search"
              @update:model-value="searchNode"
            />
          </GpFadeTransition>

          <GpBtn @click="toggleExtendedSearch" size="sm" class="tw-w-full md:tw-w-auto">
            Extended Search

            <FontAwesomeIcon
              :icon="['fas', 'fa-chevron-down']"
              :class="{
                'tw-rotate-180': showExtendedSearchOptions,
              }"
              class="tw-ml-2 tw-transition-transform"
            />
          </GpBtn>
        </template>
        <template #toolbarSelectActions>
          <p class="tw-text-lg">
            {{ selected.length }}
            {{ selected.length === 1 ? 'Node' : 'Nodes' }} selected
          </p>
          <div class="tw-mt-2 tw-flex tw-gap-4 sm:tw-mt-0">
            <GpBtn
              btn-type="flat"
              size="sm"
              outline
              :disabled="selected.length === 0"
              :icon="['fas', 'fa-plug']"
              label="Power reset"
              @click="openModal(ModalAction.HARD_RESET, selected)"
            />
            <GpBtn
              btn-type="danger"
              size="sm"
              outline
              :icon="['fas', 'fa-trash']"
              label="Delete"
              :disabled="selected.length === 0"
              @click="openModal(ModalAction.DESTROY, selected)"
            />
          </div>
        </template>
      </Toolbar>
    </template>
    <template #content>
      <GpExpandTransition>
        <div v-if="showExtendedSearchOptions" class="tw-mb-6 tw-rounded tw-bg-gp-midnight-1">
          <div class="tw-p-4">
            <h3>Extended Search</h3>
            <SearchForm
              class="tw-mt-4"
              param-prefix="node"
              :search-options="extendedSearchOptions"
              @search="performExtendedSearch"
            />
          </div>
        </div>
      </GpExpandTransition>
      <GpTable
        ref="table"
        :data="tableData"
        empty-message="No nodes created."
        pagination
        :page="pagination.currentPage"
        :max-pages="pagination.totalPages"
        v-model:selected-rows="selected"
        :is-loading="isLoading"
        loading-message="Loading nodes ..."
        @update:page="reload"
        v-playwright="'nodes-table'"
      >
        <GpTableColumn field="checkbox" selection="checkbox" :header-style="{ width: '5rem' }" />
        <GpTableColumn field="title" label="Hostname">
          <template
            #body="{
              data,
              row,
              rowIndex,
            }: {
              data: NodesTableDataRow['title'];
              row: NodesTableDataRow;
              rowIndex: number;
            }"
          >
            <NodeListToOverviewLink
              :node-id="data.id"
              :cloud-provider="row.cloudProvider"
              :title="data.title"
              v-playwright="`node-link-uuid-${rowIndex}`"
            />
          </template>
        </GpTableColumn>

        <GpTableColumn field="status" label="Status">
          <template #body="{ data }: { data: NodesTableDataRow['status'] }">
            <NodeStatus :node="data.node" />
          </template>
        </GpTableColumn>

        <GpTableColumn field="monitoringState" label="Monitoring">
          <template
            #body="{ data: monitoringState }: { data: NodesTableDataRow['monitoringState'] }"
          >
            <ServerMonitoringState :state="monitoringState" />
          </template>
        </GpTableColumn>

        <GpTableColumn field="ips" label="IPs" />

        <GpTableColumn field="tags" label="Tags">
          <template #body="{ data }: { data: NodesTableDataRow['tags'] }">
            <Tags :tags="data" :single="true" />
          </template>
        </GpTableColumn>

        <GpTableColumn field="os" label="Image">
          <template #body="{ data }: { data: NodesTableDataRow['os'] }">
            <template v-if="data">
              <span v-if="data.operatingSystem">
                <FontAwesomeIcon
                  :icon="['fab', 'windows']"
                  v-if="data.operatingSystem.family === OperatingSystemFamily.WINDOWS"
                />
                <FontAwesomeIcon v-else :icon="['fab', 'linux']" />
              </span>
              {{ data.name }}
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </template>
        </GpTableColumn>

        <GpTableColumn field="flavour" label="Flavour" />

        <GpTableColumn field="datacenter" label="Datacenter">
          <template #body="{ data }: { data: NodesTableDataRow['datacenter'] }">
            <Datacenter v-if="data.obj" :datacenter="data.obj" />
          </template>
        </GpTableColumn>
      </GpTable>
      <ConfirmModal
        :question="modalQuestion"
        :confirm-text="modalButtonText"
        :confirm-type="modalButtonClass"
        :show="modal.show"
        @close="modal.show = false"
        @confirm="confirmModal"
      />
    </template>
  </SinglePageLayout>
</template>
